import type { TFunction } from 'i18next';
import { U2000IdCheckedBoxDisplay } from '../../u2000Core/components/tables/display/U2000IdCheckedBoxDisplay';
import { U2000IdHeaderCheckedBoxDisplay } from '../../u2000Core/components/tables/display/U2000IdHeaderCheckedBoxDisplay';
import type { U2000CriteriaStaticOption, U2000TableColumn } from '../../u2000Core/components/tables/basic/U2000BasicTableList';
import { U2000_CRITERIA_LOOKUP_U3129_FIND_EMPLOYEE } from '../../u2000Core/components/tables/commons/search/U2000CriteriaLookupRegistry';
import { formatCurrency } from '../../u2000Core/tools/U2000CurrencyUtils';
import { u3130FormatPeriodDateRange } from '../../u3130TimesheetApproval/tools/u3130FormatPeriodDateRange';
import type { U3129Timesheet } from '../U3129Service';

const NS = 'u3129';

const U3129_TIMESHEET_STATUS_VALUES = ['Open', 'WaitingForApproval', 'Approve', 'WaitingForGenerateTEC', 'Close'] as const;

export function u3129BuildTimesheetStatusCriteriaOptions(t: TFunction): U2000CriteriaStaticOption[] {
  return U3129_TIMESHEET_STATUS_VALUES.map((value) => ({
    value,
    label: t(`status${value}`, { ns: NS, defaultValue: value }),
  }));
}

const CURRENCY_DISPLAY_OPTS = {
  currencySymbol: '$',
  symbolPosition: 'left' as const,
  decimalSeparator: '.' as const,
  thousandSeparator: ' ' as const,
  decimalDigits: 2,
};

function getTimesheetStatusDisplay(t: TFunction, status: string | null | undefined): string {
  if (!status?.trim()) return '';
  return t(`status${status}`, { ns: NS, defaultValue: status });
}

export function u3129GetTimesheetListColumns(t: TFunction): U2000TableColumn[] {
  const statusOptions = u3129BuildTimesheetStatusCriteriaOptions(t);
  return [
    {
      name: 'id',
      label: '',
      widthClass: 'w-[50px]',
      type: 'string',
      align: 'center',
      headerDisplay: (column: U2000TableColumn, label: string, allRowSelected: boolean, handleSelectAllRow?: (isSelected: boolean) => void) => {
        return (
          <U2000IdHeaderCheckedBoxDisplay
            column={column}
            label={label}
            disabled={false}
            allRowSelected={allRowSelected}
            handleSelectAllRow={handleSelectAllRow}
          />
        );
      },
      display: (column: U2000TableColumn, value: unknown, row: U3129Timesheet, allRowSelected: boolean, rowUpdated?: (row: U3129Timesheet) => void) => {
        const selectable = row.status === 'Open';
        return (
          <U2000IdCheckedBoxDisplay
            column={column}
            value={value}
            row={row as unknown as Record<string, unknown>}
            disabled={!selectable}
            rowUpdated={rowUpdated as unknown as (row: Record<string, unknown>) => void}
            allowRowSelection={allRowSelected}
          />
        );
      },
      isCriteriaField: false,
    },
    {
      name: 'periodDate',
      label: t('lblPeriodDate', { ns: NS }),
      widthClass: 'w-[280px]',
      type: 'date',
      align: 'left',
      inGridField: true,
      isDefaultColumn: true,
      isDefaultSortedColumn: true,
      defaultSortOrder: 'DESC',
      sortable: true,
      isCriteriaField: true,
      display: (_column, _value, row: U3129Timesheet) => {
        const r = row as unknown as Record<string, unknown>;
        const raw = r.periodDate ?? r.period_date;
        return u3130FormatPeriodDateRange(typeof raw === 'string' ? raw : '');
      },
    },
    {
      name: 'status',
      label: t('lblStatus', { ns: NS }),
      widthClass: 'w-[140px]',
      type: 'string',
      align: 'left',
      inGridField: true,
      isDefaultColumn: true,
      sortable: true,
      isCriteriaField: true,
      includeConditions: ['equals', 'in'],
      criteriaStaticOptions: statusOptions,
      display: (_column, value, row: U3129Timesheet) => {
        const rawStatus = typeof row.status === 'string' ? row.status : typeof value === 'string' ? value : '';
        return getTimesheetStatusDisplay(t, rawStatus);
      },
    },
    {
      name: 'employee',
      label: t('lblEmployee', { ns: NS }),
      widthClass: 'w-[200px]',
      type: 'string',
      align: 'left',
      inGridField: true,
      isDefaultColumn: true,
      display: (_column, _value, row: U3129Timesheet) => {
        const emp = row.employee;
        if (!emp) return '';
        const parts = [emp.firstName, emp.lastName].filter(Boolean);
        return parts.length ? parts.join(' ') : (emp.codeEmployee ?? row.employeeId ?? '');
      },
      isCriteriaField: false,
    },
    {
      name: 'codeEmployee',
      label: t('lblEmployee', { ns: NS, defaultValue: 'Employé' }),
      widthClass: 'w-[120px]',
      type: 'string',
      align: 'left',
      inGridField: true,
      isDefaultColumn: true,
      sortable: true,
      isCriteriaField: true,
      includeConditions: ['equals', 'in'],
      criteriaLookup: {
        findMethod: U2000_CRITERIA_LOOKUP_U3129_FIND_EMPLOYEE,
        searchCriterionField: 'codeEmployee',
        valueKey: 'codeEmployee',
        displayKeys: ['codeEmployee', 'firstName', 'lastName'],
        selectedDisplayKeys: ['codeEmployee', 'firstName', 'lastName'],
        placeholder: t('phCriteriaEmployee', { ns: 'common' }),
      },
      display: (_column, _value, row: U3129Timesheet) => row.employee?.codeEmployee?.trim() ?? '',
    },
    {
      name: 'totalPaidHours',
      label: t('lblTotalPaidHours', { ns: NS, defaultValue: 'Total projet' }),
      widthClass: 'w-[180px]',
      type: 'number',
      align: 'right',
      inGridField: true,
      isDefaultColumn: true,
      sortable: true,
      isCriteriaField: false,
      display: (_column, _value, row: U3129Timesheet) => {
        const total = row.totalPaidHours;
        return Number(total ?? 0).toFixed(2);
      },
    },
    {
      name: 'totalExpenses',
      label: t('lblTotalExpenses', { ns: NS, defaultValue: 'Total dépenses' }),
      widthClass: 'w-[150px]',
      type: 'number',
      align: 'right',
      inGridField: true,
      isDefaultColumn: true,
      sortable: true,
      isCriteriaField: false,
      display: (_column, _value, row: U3129Timesheet) => {
        const total = row.totalExpenses;
        if (typeof total !== 'number' || !Number.isFinite(total)) return '';
        return formatCurrency(total, CURRENCY_DISPLAY_OPTS);
      },
    },
    {
      name: 'totalEquipment',
      label: t('lblTotalEquipment', { ns: NS, defaultValue: 'Total équipement' }),
      widthClass: 'w-[150px]',
      type: 'number',
      align: 'right',
      inGridField: true,
      isDefaultColumn: true,
      sortable: true,
      isCriteriaField: false,
      display: (_column, _value, row: U3129Timesheet) => {
        const total = row.totalEquipment;
        if (typeof total !== 'number' || !Number.isFinite(total)) return '';
        return formatCurrency(total, CURRENCY_DISPLAY_OPTS);
      },
    },
  ];
}
